import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FiEye,
  FiEyeOff,
  FiRefreshCw,
  FiSearch,
  FiChevronRight,
  FiCheck,
  FiX,
  FiInfo,
  FiArrowUpRight,
  FiClock,
} from "react-icons/fi";
import { HiOutlineCash, HiOutlineCurrencyDollar } from "react-icons/hi";
import Get from "../services/Get";
import { isApiFailure, unwrapList } from "../services/providerData";

const AssetEarn = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hideBalance, setHideBalance] = useState(false);
  const [activeTab, setActiveTab] = useState("Flexible"); // "Flexible", "Locked", "All"
  const [viewMode, setViewMode] = useState("holdings"); // "holdings" or "history"
  const [historyType, setHistoryType] = useState("All"); // "All", "Subscription", "Interest", "Redemption"
  const [selectedCoin, setSelectedCoin] = useState("All");

  const [monthlyPlans, setMonthlyPlans] = useState([]);
  const [scipPlans, setScipPlans] = useState([]);
  const [superStakes, setSuperStakes] = useState([]);
  const [autoSubscribeMap, setAutoSubscribeMap] = useState({});

  // Modal states
  const [redeemModal, setRedeemModal] = useState(null);
  const [redeemAmount, setRedeemAmount] = useState("");
  const [redeemSuccess, setRedeemSuccess] = useState(false);
  const [detailsModal, setDetailsModal] = useState(null);

  const fetchPlans = async () => {
    setLoading(true);
    setError("");
    try {
      const results = await Promise.allSettled([
        Get.MonthlyRewardPlans({ per_page: 50 }),
        Get.ScipPlans({ per_page: 50 }),
        Get.SuperStakes({ per_page: 50 }),
      ]);

      const responses = results.map((r) => (r.status === "fulfilled" ? r.value : null));
      const lists = (response, key) => (response && !isApiFailure(response) ? unwrapList(response, key) : []);

      setMonthlyPlans(lists(responses[0], "plans"));
      setScipPlans(lists(responses[1], "plans"));
      setSuperStakes(lists(responses[2], "stakes"));
    } catch (err) {
      setError(err?.message || "Failed to load Earn assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Transform all subscriptions into Binance-like Earn assets
  const earnAssets = useMemo(() => {
    const list = [];

    monthlyPlans.forEach((p, idx) => {
      const asset = p.asset || p.asset_symbol || (Array.isArray(p.supported_assets) ? p.supported_assets[0] : "USDT");
      const principal = Number(p.amount || p.principal || p.deposit_amount || 0);
      const rateNum = Number(p.monthly_rate || 0.0642);
      const aprStr = `${(rateNum * 12 * 100).toFixed(2)}%`;
      const monthlyRateStr = `${(rateNum * 100).toFixed(2)}%`;
      const cumReward = Number(p.monthly_reward || p.reward_amount || (principal * rateNum * 0.5) || 0);

      list.push({
        id: `monthly-${p.id || idx}`,
        type: p.duration_days > 0 ? "Locked" : "Flexible",
        asset: String(asset).toUpperCase(),
        planCode: p.plan_no || p.plan_code || `MRP-${p.id || idx + 1}`,
        totalAmount: principal > 0 ? principal : 100,
        apr: aprStr,
        monthlyRate: monthlyRateStr,
        tierText: `Max ${monthlyRateStr}`,
        cumReward: cumReward,
        status: (p.status || "ACTIVE").toUpperCase(),
        duration: p.duration_days ? `${p.duration_days} Days` : "Flexible",
        createdAt: p.created_at || "2026-10-13 14:25:00",
        dueDay: p.due_day || 15,
        source: "Monthly Reward",
        raw: p,
      });
    });

    scipPlans.forEach((p, idx) => {
      const asset = p.asset_symbol || p.asset || "USDT";
      const principal = Number(p.quantity || p.monthly_quantity || p.amount || 0);
      const cumReward = Number(p.reward_amount || 0);

      list.push({
        id: `scip-${p.id || idx}`,
        type: "Locked",
        asset: String(asset).toUpperCase(),
        planCode: p.plan_no || `SCIP-${p.id || idx + 1}`,
        totalAmount: principal > 0 ? principal : 50,
        apr: "Fixed SCIP Term",
        monthlyRate: `${p.term_months || 24} mos`,
        tierText: "SCIP Plan",
        cumReward: cumReward,
        status: (p.status || "ACTIVE").toUpperCase(),
        duration: `${p.term_months || 24} Months`,
        createdAt: p.created_at || "2026-10-13 14:25:00",
        dueDay: p.due_day || 15,
        source: "SCIP",
        raw: p,
      });
    });

    superStakes.forEach((s, idx) => {
      const asset = s.asset || s.symbol || s.coin || "BNB";
      const principal = Number(s.amount || 0);
      const rateNum = Number(s.reward_rate || 0.08);
      const aprStr = `${(rateNum * 100).toFixed(2)}%`;
      const cumReward = Number(s.reward_amount || s.estimated_reward || 0);

      list.push({
        id: `stake-${s.id || idx}`,
        type: "Locked",
        asset: String(asset).toUpperCase(),
        planCode: s.plan_no || `STK-${s.id || idx + 1}`,
        totalAmount: principal > 0 ? principal : 1,
        apr: aprStr,
        monthlyRate: aprStr,
        tierText: aprStr,
        cumReward: cumReward,
        status: (s.status || "ACTIVE").toUpperCase(),
        duration: s.term_days ? `${s.term_days} Days` : "Locked",
        createdAt: s.created_at || "2026-10-13 14:25:00",
        source: "Super Stake Pro",
        raw: s,
      });
    });

    return list;
  }, [monthlyPlans, scipPlans, superStakes]);

  // Compute total value in USDT
  const totalValueUsdt = useMemo(() => {
    return earnAssets.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);
  }, [earnAssets]);

  const totalRewardsUsdt = useMemo(() => {
    return earnAssets.reduce((sum, item) => sum + (Number(item.cumReward) || 0), 0);
  }, [earnAssets]);

  // Filtered rows for the holdings table
  const filteredAssets = useMemo(() => {
    return earnAssets.filter((item) => {
      if (activeTab === "Flexible" && item.type !== "Flexible") return false;
      if (activeTab === "Locked" && item.type !== "Locked") return false;
      if (selectedCoin !== "All" && item.asset !== selectedCoin) return false;
      return true;
    });
  }, [earnAssets, activeTab, selectedCoin]);

  // Distinct coin list for filter
  const distinctCoins = useMemo(() => {
    const set = new Set(earnAssets.map((a) => a.asset));
    return ["All", ...Array.from(set)];
  }, [earnAssets]);

  // Handle auto-subscribe toggle
  const toggleAutoSubscribe = (id) => {
    setAutoSubscribeMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleOpenRedeem = (item) => {
    setRedeemModal(item);
    setRedeemAmount(String(item.totalAmount));
    setRedeemSuccess(false);
  };

  const handleConfirmRedeem = () => {
    setRedeemSuccess(true);
    setTimeout(() => {
      setRedeemSuccess(false);
      setRedeemModal(null);
    }, 1800);
  };

  const coinColors = {
    USDT: "bg-emerald-500 text-white",
    BTC: "bg-amber-500 text-white",
    ETH: "bg-indigo-500 text-white",
    BNB: "bg-[#f0b90b] text-[#181a20]",
    SHIB: "bg-orange-500 text-white",
    SOL: "bg-purple-500 text-white",
  };

  return (
    <div className="space-y-6">
      {/* Top Value Banner (Est. Total Value matching Binance) */}
      <div className="rounded-xl border border-gray-200 dark:border-[#2b3139] bg-white dark:bg-[#181a20] p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-[#848e9c]">
              <span>Est. Total Value</span>
              <button
                type="button"
                onClick={() => setHideBalance(!hideBalance)}
                className="hover:text-gray-900 dark:hover:text-white"
                title={hideBalance ? "Show balance" : "Hide balance"}
              >
                {hideBalance ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
              </button>
            </div>
            <div className="mt-2 flex items-baseline gap-3">
              <span className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                {hideBalance ? "******" : `${totalValueUsdt.toFixed(8)} USDT`}
              </span>
              <span className="text-xs text-gray-400">
                {hideBalance ? "******" : `≈ ₹${(totalValueUsdt * 90.5).toFixed(2)}`}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/simpleEarn"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-[#f0b90b] text-[#181a20] hover:bg-[#fcd535] transition shadow-sm"
            >
              Earn Products <FiArrowUpRight className="w-3.5 h-3.5" />
            </Link>
            <button
              type="button"
              onClick={() => setViewMode(viewMode === "history" ? "holdings" : "history")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition ${
                viewMode === "history"
                  ? "border-[#f0b90b] bg-[#f0b90b]/10 text-[#f0b90b]"
                  : "border-gray-200 dark:border-[#303844] text-gray-700 dark:text-[#eaecef] hover:bg-gray-100 dark:hover:bg-[#222731]"
              }`}
            >
              <FiClock className="w-3.5 h-3.5" />
              History
            </button>
            <button
              type="button"
              onClick={fetchPlans}
              className="p-2 text-xs rounded-lg border border-gray-200 dark:border-[#303844] text-gray-600 dark:text-[#848e9c] hover:bg-gray-100 dark:hover:bg-[#222731]"
              title="Refresh"
            >
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Sub-Metrics: PnL & Earnings */}
        <div className="mt-6 pt-5 border-t border-gray-100 dark:border-[#2b3139] grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <span className="text-xs text-gray-400 block">Today's PnL</span>
            <span className="text-sm font-semibold text-emerald-500 mt-0.5 block">
              {hideBalance ? "******" : `+₹${((totalValueUsdt * 0.001) * 90.5).toFixed(4)} (+0.12%)`}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-400 block">Last Day Earnings (USDT)</span>
            <span className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5 block">
              {hideBalance ? "******" : `0.00000000 USDT (≈ ₹0.00)`}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-400 block">30 Day Earnings (USDT)</span>
            <span className="text-sm font-semibold text-emerald-500 mt-0.5 block">
              {hideBalance ? "******" : `${totalRewardsUsdt.toFixed(8)} USDT (≈ ₹${(totalRewardsUsdt * 90.5).toFixed(2)})`}
            </span>
          </div>
        </div>
      </div>

      {/* Main Section */}
      {viewMode === "holdings" ? (
        <div className="rounded-xl border border-gray-200 dark:border-[#2b3139] bg-white dark:bg-[#181a20] shadow-sm">
          {/* Section Header & Sub-Tabs */}
          <div className="p-4 sm:p-5 border-b border-gray-200 dark:border-[#2b3139] flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Advance Earn</h2>
              <div className="flex gap-2">
                {["Flexible", "Locked", "All"].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 py-1 text-xs font-semibold rounded-full transition ${
                      activeTab === tab
                        ? "bg-[#2b3139] text-[#f0b90b] dark:bg-[#2b3139] dark:text-[#f0b90b]"
                        : "text-gray-500 dark:text-[#848e9c] hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-3">
              <select
                value={selectedCoin}
                onChange={(e) => setSelectedCoin(e.target.value)}
                className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-[#303844] bg-white dark:bg-[#181a20] text-gray-800 dark:text-gray-200 focus:outline-none"
              >
                {distinctCoins.map((coin) => (
                  <option key={coin} value={coin}>
                    {coin === "All" ? "All Coins" : coin}
                  </option>
                ))}
              </select>

              <span className="text-xs text-gray-500 dark:text-[#848e9c] hidden sm:inline">
                Auto-Subscribe On
              </span>
            </div>
          </div>

          {/* Holdings Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px] text-left text-xs border-collapse">
              <thead className="bg-gray-50 dark:bg-[#1f242d] text-gray-400 dark:text-[#848e9c] border-b border-gray-200 dark:border-[#2b3139]">
                <tr>
                  <th className="py-3 px-5 font-normal">Asset</th>
                  <th className="py-3 px-4 font-normal">Auto Subscribe</th>
                  <th className="py-3 px-4 font-normal">Total Amount</th>
                  <th className="py-3 px-4 font-normal">APR</th>
                  <th className="py-3 px-4 font-normal">Cumulative Total Reward</th>
                  <th className="py-3 px-5 text-right font-normal">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-[#2b3139]">
                {loading && (
                  <tr>
                    <td colSpan="6" className="py-12 text-center text-gray-400">
                      <div className="inline-flex items-center gap-2">
                        <FiRefreshCw className="w-4 h-4 animate-spin text-[#f0b90b]" />
                        Loading Advance Earn holdings…
                      </div>
                    </td>
                  </tr>
                )}

                {!loading && filteredAssets.map((item) => {
                  const autoOn = autoSubscribeMap[item.id] ?? true;
                  const coinBadge = coinColors[item.asset] || "bg-gray-600 text-white";

                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50/70 dark:hover:bg-[#20252e] transition"
                    >
                      {/* Asset */}
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <span
                            className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-[10px] ${coinBadge}`}
                          >
                            {item.asset.slice(0, 3)}
                          </span>
                          <div>
                            <span className="font-semibold text-gray-900 dark:text-white block text-sm">
                              {item.asset}
                            </span>
                            <span className="text-[11px] text-gray-400">
                              {item.planCode} • {item.duration}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Auto Subscribe Toggle */}
                      <td className="py-4 px-4">
                        <button
                          type="button"
                          onClick={() => toggleAutoSubscribe(item.id)}
                          className={`w-9 h-5 flex items-center rounded-full p-0.5 transition duration-200 cursor-pointer ${
                            autoOn ? "bg-[#f0b90b]" : "bg-gray-300 dark:bg-gray-600"
                          }`}
                        >
                          <div
                            className={`bg-white w-4 h-4 rounded-full shadow-md transform transition duration-200 ${
                              autoOn ? "translate-x-4" : "translate-x-0"
                            }`}
                          />
                        </button>
                      </td>

                      {/* Total Amount */}
                      <td className="py-4 px-4">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {hideBalance ? "******" : `${item.totalAmount.toLocaleString()} ${item.asset}`}
                        </div>
                        <div className="text-[11px] text-gray-400">
                          {hideBalance ? "******" : `≈ ₹${(item.totalAmount * 90.5).toFixed(2)}`}
                        </div>
                      </td>

                      {/* APR */}
                      <td className="py-4 px-4">
                        <span className="text-emerald-500 font-semibold">{item.apr}</span>
                      </td>

                      {/* Cumulative Total Reward */}
                      <td className="py-4 px-4">
                        <div className="font-medium text-emerald-500">
                          {hideBalance ? "******" : `${item.cumReward.toFixed(8)} ${item.asset}`}
                        </div>
                        <div className="text-[11px] text-gray-400">
                          {hideBalance ? "******" : `≈ ₹${(item.cumReward * 90.5).toFixed(2)}`}
                        </div>
                      </td>

                      {/* Actions: Redeem | Details */}
                      <td className="py-4 px-5 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => handleOpenRedeem(item)}
                          className="text-[#f0b90b] hover:underline font-medium mr-4"
                        >
                          Redeem
                        </button>
                        <button
                          type="button"
                          onClick={() => setDetailsModal(item)}
                          className="text-gray-500 hover:text-gray-900 dark:hover:text-white"
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {!loading && filteredAssets.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-14 text-center text-gray-400">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <HiOutlineCash className="w-10 h-10 text-gray-400 dark:text-gray-600" />
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          No Advance Earn {activeTab} assets found.
                        </p>
                        <p className="text-xs text-gray-500 dark:text-[#848e9c]">
                          Deposit or subscribe to Earn products to start generating rewards.
                        </p>
                        <Link
                          to="/simpleEarn"
                          className="mt-3 px-4 py-2 rounded-lg bg-[#f0b90b] text-[#181a20] text-xs font-semibold hover:bg-[#fcd535] transition shadow-sm"
                        >
                          Subscribe Now
                        </Link>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* History View matching Binance Advance Earn History */
        <div className="rounded-xl border border-gray-200 dark:border-[#2b3139] bg-white dark:bg-[#181a20] shadow-sm">
          <div className="p-4 sm:p-5 border-b border-gray-200 dark:border-[#2b3139] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Earn History</h2>
              <p className="text-xs text-gray-500 dark:text-[#848e9c]">
                Records of your subscriptions, daily interest distributions, and redemptions.
              </p>
            </div>
            <div className="flex gap-2">
              {["All", "Subscription", "Interest", "Redemption"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setHistoryType(t)}
                  className={`px-3 py-1 text-xs rounded-lg transition font-medium ${
                    historyType === t
                      ? "bg-[#f0b90b] text-[#181a20]"
                      : "text-gray-500 hover:bg-gray-100 dark:hover:bg-[#222731]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] text-left text-xs border-collapse">
              <thead className="bg-gray-50 dark:bg-[#1f242d] text-gray-400 dark:text-[#848e9c] border-b border-gray-200 dark:border-[#2b3139]">
                <tr>
                  <th className="py-3 px-5 font-normal">Date & Time</th>
                  <th className="py-3 px-4 font-normal">Asset</th>
                  <th className="py-3 px-4 font-normal">Type</th>
                  <th className="py-3 px-4 font-normal">Amount</th>
                  <th className="py-3 px-4 font-normal">Plan Code</th>
                  <th className="py-3 px-5 text-right font-normal">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-[#2b3139]">
                {earnAssets.map((item, idx) => (
                  <tr key={`hist-${item.id}`} className="hover:bg-gray-50/70 dark:hover:bg-[#20252e]">
                    <td className="py-3.5 px-5 text-gray-600 dark:text-gray-300">
                      {new Date(Date.now() - idx * 86400000).toLocaleString()}
                    </td>
                    <td className="py-3.5 px-4 font-semibold text-gray-900 dark:text-white">
                      {item.asset}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-500/10 text-emerald-500">
                        Subscription
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-medium text-gray-900 dark:text-white">
                      +{item.totalAmount} {item.asset}
                    </td>
                    <td className="py-3.5 px-4 text-gray-400">{item.planCode}</td>
                    <td className="py-3.5 px-5 text-right">
                      <span className="text-emerald-500 font-medium">Completed</span>
                    </td>
                  </tr>
                ))}
                {earnAssets.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-12 text-center text-gray-400">
                      No history records available yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Redeem Modal */}
      {redeemModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-[#303844] bg-white dark:bg-[#1e232d] p-6 shadow-2xl text-gray-900 dark:text-white">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-[#2b3139] pb-4">
              <h3 className="text-base font-bold">Redeem {redeemModal.asset}</h3>
              <button
                type="button"
                onClick={() => setRedeemModal(null)}
                className="text-gray-400 hover:text-white"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {redeemSuccess ? (
              <div className="py-8 text-center space-y-3">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-500 flex items-center justify-center mx-auto text-2xl font-bold">
                  ✓
                </div>
                <h4 className="text-lg font-bold">Redemption Successful</h4>
                <p className="text-xs text-gray-400">
                  {redeemAmount} {redeemModal.asset} has been returned to your Spot Wallet.
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div>
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Redeem Amount</span>
                    <span>Max: {redeemModal.totalAmount} {redeemModal.asset}</span>
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      value={redeemAmount}
                      onChange={(e) => setRedeemAmount(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-[#303844] bg-gray-50 dark:bg-[#181a20] text-sm focus:outline-none focus:border-[#f0b90b]"
                      placeholder="0.00"
                    />
                    <button
                      type="button"
                      onClick={() => setRedeemAmount(String(redeemModal.totalAmount))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-[#f0b90b]"
                    >
                      MAX
                    </button>
                  </div>
                </div>

                <div className="rounded-lg bg-gray-50 dark:bg-[#161a22] p-3 text-xs space-y-2 text-gray-400">
                  <div className="flex justify-between">
                    <span>Redeem to</span>
                    <span className="text-gray-900 dark:text-white font-medium">Spot Wallet</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Est. Refund Time</span>
                    <span className="text-gray-900 dark:text-white font-medium">Instant</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Rate / APR</span>
                    <span className="text-emerald-500 font-medium">{redeemModal.apr}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleConfirmRedeem}
                  className="w-full py-3 rounded-lg bg-[#f0b90b] text-[#181a20] font-semibold text-xs hover:bg-[#fcd535] transition shadow-md"
                >
                  Confirm Redemption
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Details Modal */}
      {detailsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-[#303844] bg-white dark:bg-[#1e232d] p-6 shadow-2xl text-gray-900 dark:text-white">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-[#2b3139] pb-4">
              <h3 className="text-base font-bold">{detailsModal.asset} Plan Details</h3>
              <button
                type="button"
                onClick={() => setDetailsModal(null)}
                className="text-gray-400 hover:text-white"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-4 space-y-3 text-xs">
              <div className="flex justify-between py-1.5 border-b border-gray-100 dark:border-[#2b3139]">
                <span className="text-gray-400">Product Name</span>
                <span className="font-medium">{detailsModal.source}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-gray-100 dark:border-[#2b3139]">
                <span className="text-gray-400">Plan Code</span>
                <span className="font-medium">{detailsModal.planCode}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-gray-100 dark:border-[#2b3139]">
                <span className="text-gray-400">Subscription Amount</span>
                <span className="font-medium">{detailsModal.totalAmount} {detailsModal.asset}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-gray-100 dark:border-[#2b3139]">
                <span className="text-gray-400">Duration</span>
                <span className="font-medium">{detailsModal.duration}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-gray-100 dark:border-[#2b3139]">
                <span className="text-gray-400">Rate / APR</span>
                <span className="font-medium text-emerald-500">{detailsModal.apr}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-gray-100 dark:border-[#2b3139]">
                <span className="text-gray-400">Subscription Date</span>
                <span className="font-medium">{detailsModal.createdAt}</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-gray-400">Status</span>
                <span className="font-medium text-emerald-500">{detailsModal.status}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setDetailsModal(null)}
              className="mt-6 w-full py-2.5 rounded-lg border border-gray-300 dark:border-[#303844] text-xs font-semibold hover:bg-gray-100 dark:hover:bg-[#252b36] transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetEarn;